import axios from 'axios';

// Use same origin in production, localhost for development
const API_URL = process.env.REACT_APP_API_URL || '';

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true
});

// Gate authentication (password protection)
export const gateApi = {
  login: (password) => api.post('/gate/login', { password }),
  logout: () => api.post('/gate/logout'),
  check: () => api.get('/gate/check')
};

// Auth endpoints (demo user selection)
export const authApi = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  ssoCallback: () => api.post('/auth/sso/callback'),
  getTenants: () => api.get('/auth/tenants'),
  selectTenant: (tenantId) => api.post('/auth/tenants/select', { tenantId }),
  getCurrentUser: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  switchPersona: (persona) => api.post('/auth/switch-persona', { persona })
};

// Assets endpoints
export const assetsApi = {
  getAll: (filters = {}) => api.get('/assets', { params: filters }),
  getSummary: () => api.get('/assets/summary'),
  getPartsInTransit: () => api.get('/assets/parts-in-transit'),
  getById: (id) => api.get(`/assets/${id}`),
  getAlerts: (id) => api.get(`/assets/${id}/alerts`),
  getMaintenanceHistory: (id) => api.get(`/assets/${id}/maintenance-history`),
  getServiceBulletins: (id) => api.get(`/assets/${id}/service-bulletins`),
  getPrediction: (id) => api.get(`/assets/${id}/prediction`)
};

// Jobs endpoints
export const jobsApi = {
  getAll: (filters = {}) => api.get('/jobs', { params: filters }),
  getStats: () => api.get('/jobs/stats'),
  getById: (id) => api.get(`/jobs/${id}`),
  create: (jobData) => api.post('/jobs', jobData),
  updateStatus: (id, status) => api.patch(`/jobs/${id}/status`, { status }),
  getAiRecommendation: (id) => api.get(`/jobs/${id}/ai-recommendation`)
};

// Incidents endpoints
export const incidentsApi = {
  getAll: (filters = {}) => api.get('/incidents', { params: filters }),
  getStats: () => api.get('/incidents/stats'),
  getById: (id) => api.get(`/incidents/${id}`),
  getTimeline: (id) => api.get(`/incidents/${id}/timeline`),
  addTimelineEvent: (id, event) => api.post(`/incidents/${id}/timeline`, event),
  getAiSummary: (id) => api.get(`/incidents/${id}/ai-summary`)
};

// Telemetry endpoints
export const telemetryApi = {
  getData: (assetId, metrics, hours) => api.get(`/telemetry/${assetId}`, { params: { metrics, hours } }),
  getCurrent: (assetId) => api.get(`/telemetry/${assetId}/current`),
  getThresholds: () => api.get('/telemetry/config/thresholds'),
  getAlerts: (assetId) => api.get(`/telemetry/${assetId}/alerts`),
  getTrend: (assetId, metric, days) => api.get(`/telemetry/${assetId}/trend/${metric}`, { params: { days } })
};

// AI endpoints
export const aiApi = {
  speechToText: (audioBlob) => {
    const formData = new FormData();
    formData.append('audio', audioBlob, 'audio.webm');
    return api.post('/ai/speech-to-text', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  chat: (message, context = [], page = 'fleet') => api.post('/ai/chat', { message, context, page }),
  getSuggestions: (page = 'fleet') => api.get('/ai/suggestions', { params: { page } }),
  explain: (assetId, metric, context) => api.post('/ai/explain', { assetId, metric, context })
};

// Reports endpoints
export const reportsApi = {
  getMonthly: (month, year) => api.get('/reports/monthly', { params: { month, year } }),
  getKpi: (period) => api.get('/reports/kpi', { params: { period } }),
  getTrends: (metric, days) => api.get(`/reports/trends/${metric}`, { params: { days } }),
  getComparison: () => api.get('/reports/comparison')
};

// WebSocket connection
export const createWebSocket = (onMessage, onOpen, onClose) => {
  // Use same origin for WebSocket if API_URL is empty
  const wsBase = API_URL || window.location.origin;
  const wsUrl = wsBase.replace('http', 'ws');
  const ws = new WebSocket(wsUrl);

  ws.onopen = () => {
    console.log('WebSocket connected');
    onOpen?.();
  };

  ws.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data);
      onMessage?.(data);
    } catch (error) {
      console.error('WebSocket message parse error:', error);
    }
  };

  ws.onclose = () => {
    console.log('WebSocket disconnected');
    onClose?.();
  };

  ws.onerror = (error) => {
    console.error('WebSocket error:', error);
  };

  return ws;
};

export default api;
